import React, { useState, useEffect, useRef, useCallback } from "react";
import { Upload, X, ShoppingCart, RotateCcw, Loader, AlertCircle, Info } from "lucide-react";
import "@google/model-viewer";

const AI3DModeling = ({ onAddToCart, onRequestLogin, user, onShowInfo }) => {
  const [selectedImages, setSelectedImages] = useState([]);
  const [imagePositions, setImagePositions] = useState([]); // 각 이미지의 위치 정보
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState("");
  const [usage, setUsage] = useState({ usage: 0, limit: 3, remaining: 3 });
  const [userModels, setUserModels] = useState([]);
  const [queueInfo, setQueueInfo] = useState({ queuedCount: 0, isProcessing: false, queuePosition: null });
  const [isLoadingModels, setIsLoadingModels] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [showUsageModal, setShowUsageModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showProcessingModal, setShowProcessingModal] = useState(false);
  const [showCompletedModal, setShowCompletedModal] = useState(false);
  const [selectedModel, setSelectedModel] = useState(null);
  const [processingModel, setProcessingModel] = useState(null);
  const [showAllModels, setShowAllModels] = useState(false);
  const [showLoadModal, setShowLoadModal] = useState(false);
  const [loadId, setLoadId] = useState("");

  const fileInputRef = useRef(null);

  // 사용량 조회
  const loadUsage = useCallback(async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE}/api/ai/usage`, {
        headers: {
          Authorization: localStorage.getItem("auth_token"),
        },
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setUsage(result);
        }
      }
    } catch (err) {
      console.error("사용량 조회 오류:", err);
    }
  }, []);

  // 사용자 AI 모델 목록 조회
  const loadUserModels = useCallback(async () => {
    setIsLoadingModels(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE}/api/ai/models`, {
        headers: {
          Authorization: localStorage.getItem("auth_token"),
        },
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setUserModels(result.models || []);
          if (result.queue) setQueueInfo(result.queue);
        }
      }
    } catch (err) {
      console.error("모델 목록 조회 오류:", err);
    } finally {
      setIsLoadingModels(false);
    }
  }, []);

  // 인증 확인 및 데이터 로드
  const checkAuthAndLoadData = useCallback(async () => {
    const token = localStorage.getItem("auth_token");
    if (token) {
      setIsLoggedIn(true);
      await Promise.all([loadUsage(), loadUserModels()]);
    } else {
      setIsLoggedIn(false);
    }
  }, [loadUsage, loadUserModels]);

  // 컴포넌트 마운트 시 초기화
  useEffect(() => {
    checkAuthAndLoadData();
  }, [checkAuthAndLoadData]);

  // URL 파라미터에서 creationId를 가져와 로드 모달을 프리필
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const creationId = params.get("creationId");
    if (creationId) {
      setLoadId(creationId);
      setShowLoadModal(true);
    }
  }, []);

  // 생성/대기 중인 모델이 있으면 폴링
  useEffect(() => {
    if (!isLoggedIn) return;
    const hasActive = userModels.some((m) => m.status === "processing" || m.status === "queued");
    if (!hasActive) return;
    const id = setInterval(() => {
      loadUserModels();
    }, 4000);
    return () => clearInterval(id);
  }, [isLoggedIn, userModels, loadUserModels]);

  // user prop 변화 감지해서 로그인 상태 업데이트
  useEffect(() => {
    if (user) {
      setIsLoggedIn(true);
      // 로그인이 감지되면 데이터 새로고침
      Promise.all([loadUsage(), loadUserModels()]);
    } else {
      setIsLoggedIn(false);
      // 로그아웃되면 데이터 초기화
      setUsage({ usage: 0, limit: 3, remaining: 3 });
      setUserModels([]);
    }
  }, [user, loadUsage, loadUserModels]);

  // 이미지 해상도 검사 (비동기)
  const validateImageDimensions = (file) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(file);

      img.onload = () => {
        URL.revokeObjectURL(url); // 메모리 정리
        const { width, height } = img;

        if (width > 5000 || height > 5000) {
          resolve(`이미지 해상도가 너무 큽니다. (현재: ${width}x${height}, 최대: 5000x5000)`);
        } else {
          resolve(null); // 유효함
        }
      };

      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject("이미지를 로드할 수 없습니다.");
      };

      img.src = url;
    });
  };

  // 이미지 파일 유효성 검사
  const validateImage = async (file) => {
    const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
    const maxSize = 10 * 1024 * 1024; // 10MB

    if (!allowedTypes.includes(file.type)) {
      return "지원하지 않는 파일 형식입니다. (jpg, jpeg, png만 지원)";
    }

    if (file.size > maxSize) {
      return "파일 크기가 너무 큽니다. (최대 10MB)";
    }

    // 해상도 검사 추가
    try {
      const dimensionError = await validateImageDimensions(file);
      if (dimensionError) {
        return dimensionError;
      }
    } catch (error) {
      return error;
    }

    return null;
  };

  // 파일 추가 처리 (공통)
  const addFiles = async (files) => {
    if (!files || files.length === 0) return;
    if (selectedImages.length + files.length > 4) {
      setError("최대 4장의 이미지만 업로드할 수 있습니다.");
      return;
    }

    setError("이미지 검증 중...");
    let hasError = false;
    const validFiles = [];

    for (const file of files) {
      try {
        const validationError = await validateImage(file);
        if (validationError) {
          setError(validationError);
          hasError = true;
          break;
        }
        validFiles.push(file);
      } catch (error) {
        setError(`이미지 검증 중 오류가 발생했습니다: ${error}`);
        hasError = true;
        break;
      }
    }

    if (!hasError) {
      setSelectedImages((prev) => [...prev, ...validFiles]);
      const defaultPositions = ["front", "left", "right", "back"];
      const newPositions = validFiles.map((_, index) => {
        const positionIndex = selectedImages.length + index;
        return defaultPositions[positionIndex] || "front";
      });
      setImagePositions((prev) => [...prev, ...newPositions]);
      setError("");
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // 드래그 앤 드롭 처리
  const handleDrop = async (e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer?.files || []);
    await addFiles(files);
  };

  // 이미지 선택 처리 (input)
  const handleImageSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    await addFiles(files);
  };

  // 이미지 제거
  const removeImage = (index) => {
    setSelectedImages((prev) => prev.filter((_, i) => i !== index));
    setImagePositions((prev) => prev.filter((_, i) => i !== index));
  };

  // 이미지 위치 변경
  const changeImagePosition = (index, newPosition) => {
    setImagePositions((prev) => {
      const newPositions = [...prev];
      newPositions[index] = newPosition;
      return newPositions;
    });
  };

  // 이미지 URL 생성
  const getImageUrl = (file) => {
    return URL.createObjectURL(file);
  };

  // 3D 모델 생성 요청
  const generateModel = async () => {
    // 로그인 확인
    if (!isLoggedIn) {
      if (onRequestLogin) {
        onRequestLogin();
      }
      return;
    }

    // 사용량 확인
    if (usage.remaining <= 0) {
      setShowUsageModal(true);
      return;
    }

    if (selectedImages.length === 0) {
      setError("최소 1장의 이미지를 선택해주세요.");
      return;
    }

    // front 이미지가 포함되어 있는지 확인
    if (!imagePositions.includes("front")) {
      setError("전면(front) 이미지는 반드시 포함되어야 합니다.");
      return;
    }

    setIsUploading(true);
    setError("");

    try {
      const formData = new FormData();
      selectedImages.forEach((image) => {
        formData.append("images", image);
      });

      // 위치 정보도 함께 전송
      imagePositions.forEach((position) => {
        formData.append("positions", position);
      });

      const response = await fetch(`${process.env.REACT_APP_API_BASE}/api/ai/generate-3d-model`, {
        method: "POST",
        headers: {
          Authorization: localStorage.getItem("auth_token"),
        },
        body: formData,
      });

      if (response.status === 429) {
        setShowUsageModal(true);
        setIsUploading(false);
        return;
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "모델 생성 요청에 실패했습니다.");
      }

      const result = await response.json();

      if (result.success) {
        // 성공적으로 요청됨 - 성공 모달 표시
        setShowSuccessModal(true);
        // 모델 목록을 새로고침
        await Promise.all([loadUsage(), loadUserModels()]);
        setSelectedImages([]);
        setImagePositions([]); // 위치 정보도 초기화
        setError("");
      }
    } catch (err) {
      console.error("3D 모델 생성 오류:", err);
      setError(err.message || "3D 모델 생성 중 오류가 발생했습니다.");
    } finally {
      setIsUploading(false);
    }
  };

  // 장바구니에 추가
  const addToCart = async (modelUuid) => {
    if (!onAddToCart) return;

    try {
      // AI 견적 생성 API 호출
      const response = await fetch(`${process.env.REACT_APP_API_BASE}/api/ai/estimate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: localStorage.getItem("auth_token"),
        },
        body: JSON.stringify({
          modelUuid: modelUuid,
        }),
      });

      if (!response.ok) {
        throw new Error("견적 생성에 실패했습니다.");
      }

      const result = await response.json();

      if (result.success && result.data) {
        // 생성된 견적을 장바구니에 추가
        onAddToCart(result.data);
        // 모델 목록 새로고침 (견적 UUID 업데이트)
        await loadUserModels();
      } else {
        throw new Error("견적 데이터를 받아오는데 실패했습니다.");
      }
    } catch (err) {
      console.error("견적 생성 오류:", err);
      setError(err.message || "견적 생성 중 오류가 발생했습니다.");
    }
  };

  // 모델 클릭 핸들러
  const handleModelClick = (model) => {
    if (model.status === "queued") {
      setProcessingModel(model);
      setShowProcessingModal(true);
    } else if (model.status === "processing") {
      setProcessingModel(model);
      setShowProcessingModal(true);
    } else if (model.status === "completed") {
      setSelectedModel(model);
      setShowCompletedModal(true);
    }
  };

  // 초기화
  const resetAll = () => {
    setSelectedImages([]);
    setImagePositions([]); // 위치 정보도 초기화
    setError("");
    setIsUploading(false);
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-6">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-2xl font-bold text-gray-900 flex items-center">
          AI 3D 모델링
          {onShowInfo && (
            <button onClick={onShowInfo} className="p-1 text-gray-400 hover:text-teal-500 transition-colors" title="안내사항 보기">
              <Info className="w-5 h-5" />
            </button>
          )}
        </h2>
        {selectedImages.length > 0 && (
          <button onClick={resetAll} className="flex items-center px-3 py-2 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50">
            <RotateCcw className="w-4 h-4 mr-1" />
            다시 시작
          </button>
        )}
      </div>

      {/* 사용량 표시 */}
      {isLoggedIn && (
        <div className="mb-4 text-sm text-gray-600">
          일일 사용량: {usage.usage}/{usage.limit}{" "}
          {usage.remaining > 0 ? (
            `(${usage.remaining}회 남음)`
          ) : (
            <>
              (사용 완료,{" "}
              <button
                onClick={async () => {
                  try {
                    const res = await fetch(`${process.env.REACT_APP_API_BASE}/api/ai/usage/reset`, {
                      method: "POST",
                      headers: { Authorization: localStorage.getItem("auth_token") },
                    });
                    if (!res.ok) throw new Error("초기화 요청에 실패했습니다.");
                    const data = await res.json();
                    if (data.success && data.pay_code) {
                      window.location.href = `${process.env.REACT_APP_PAY_BASE}/${data.pay_code}`;
                    }
                  } catch (err) {
                    console.error("사용량 초기화 오류:", err);
                    setError(err.message || "사용량 초기화 중 오류가 발생했습니다.");
                  }
                }}
                className="text-teal-600 hover:text-teal-700 underline"
              >
                초기화
              </button>
              )
            </>
          )}
        </div>
      )}

      {/* 이미지 업로드 섹션 */}
      <div className="space-y-4 mb-8">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">이미지 업로드 (1-4장)</label>
          <div className="mb-3 p-3 bg-teal-50 border-l-4 border-teal-400 rounded-r-lg">
            <p className="text-sm text-teal-800">
              <span className="font-medium">💡TIP:</span> 다양한 구도의 이미지를 업로드하여 3D 모델을 생성할 수 있습니다. (전면 필수)
            </p>
            <p className="text-xs text-teal-600 mt-1">🔀 이미지 업로드 후 구도 태그를 클릭하여 반드시 올바르게 변경 후 사용해주세요.</p>
          </div>
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-teal-400 transition-colors">
            <input ref={fileInputRef} type="file" multiple accept="image/jpeg,image/jpg,image/png" onChange={handleImageSelect} className="hidden" id="ai-image-upload" />
            <label htmlFor="ai-image-upload" onDrop={handleDrop} onDragOver={(e) => e.preventDefault()} className="cursor-pointer flex flex-col items-center">
              <Upload className="w-12 h-12 text-teal-500 mb-4 opacity-70" />
              <p className="text-gray-600 font-medium">파일을 드래그하여 놓거나 클릭하여 선택하세요</p>
              <p className="text-gray-400 text-sm mt-1">JPG, PNG 형식, 최대 10MB, 5000×5000 이하</p>
            </label>
          </div>
        </div>

        {/* 선택된 이미지 미리보기 */}
        {selectedImages.length > 0 && (
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-3">선택된 이미지 ({selectedImages.length}/4)</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {selectedImages.map((image, index) => {
                const positionLabels = { front: "전면", left: "좌측", right: "우측", back: "후면" };
                const positionColors = { front: "bg-red-500", left: "bg-blue-500", right: "bg-green-500", back: "bg-purple-500" };
                const currentPosition = imagePositions[index] || "front";
                const availablePositions = ["front", "left", "right", "back"];

                return (
                  <div key={index} className="relative group">
                    <img src={getImageUrl(image)} alt={`선택된 이미지 ${index + 1}`} className="w-full h-24 object-cover rounded-lg border" />

                    {/* 위치 선택 드롭다운 */}
                    <div className="absolute top-2 left-2">
                      <select value={currentPosition} onChange={(e) => changeImagePosition(index, e.target.value)} className={`${positionColors[currentPosition]} text-white text-xs px-2 py-1 rounded-full font-medium border-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50`} style={{ appearance: "none", backgroundImage: "none" }}>
                        {availablePositions.map((position) => (
                          <option key={position} value={position} className="bg-white text-black">
                            {positionLabels[position]}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* 삭제 버튼 */}
                    <button onClick={() => removeImage(index)} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <X className="w-4 h-4" />
                    </button>

                    {/* 파일명 */}
                    <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs p-1 rounded-b-lg truncate">{image.name}</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* 에러 메시지 */}
        {error && (
          <div className="flex items-center p-3 bg-red-50 border border-red-200 rounded-lg">
            <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
            <span className="text-red-700 text-sm">{error}</span>
          </div>
        )}

        {/* 생성 시작 버튼 */}
        <button onClick={generateModel} disabled={selectedImages.length === 0 || isUploading} className="w-full bg-teal-500 text-white py-3 rounded-lg font-medium hover:bg-teal-600 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors">
          {isUploading ? "업로드중..." : !isLoggedIn ? "로그인하고 3D 모델 생성하기" : usage.remaining <= 0 ? "일일 사용량 부족" : "3D 모델 생성하기"}
        </button>
      </div>

      {/* AI 모델 목록 */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">내 AI 모델</h3>
          <div className="flex items-center gap-2">
            <button onClick={() => setShowLoadModal(true)} className="text-sm text-teal-600 hover:text-teal-700 disabled:text-gray-400">
              불러오기
            </button>
            <button onClick={loadUserModels} disabled={isLoadingModels || !isLoggedIn} className="text-sm text-teal-600 hover:text-teal-700 disabled:text-gray-400">
              {isLoadingModels ? "로딩중..." : "새로고침"}
            </button>
          </div>
        </div>

        {!isLoggedIn ? (
          <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
            <p className="text-gray-600 mb-4">AI 모델을 생성하려면 로그인이 필요합니다.</p>
            {onRequestLogin && (
              <button onClick={onRequestLogin} className="px-6 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors">
                로그인
              </button>
            )}
          </div>
        ) : userModels.length === 0 ? (
          <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
            <p className="text-gray-600">아직 생성된 AI 모델이 없습니다.</p>
            <p className="text-sm text-gray-500 mt-1">위에서 이미지를 업로드하여 첫 모델을 생성해보세요!</p>
          </div>
        ) : (
          <div className="space-y-6">
            {(() => {
              const modelsToDisplay = showAllModels ? userModels : userModels.slice(0, 4);
              return (
                <>
                  {/* 대기 중 섹션 */}
                  {modelsToDisplay.some((m) => m.status === "queued") && (
                    <div>
                      <div className="flex items-center mb-2">
                        <span className="text-sm font-semibold text-gray-700">대기 중</span>
                        <span className="ml-2 text-xs text-gray-500">({modelsToDisplay.filter((m) => m.status === "queued").length})</span>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {modelsToDisplay
                          .filter((m) => m.status === "queued")
                          .map((model) => (
                            <div key={model.uuid} className="border rounded-lg p-4 pb-1 space-y-3 cursor-pointer hover:shadow-md transition-shadow" onClick={() => handleModelClick(model)}>
                              <div className="flex items-center justify-between">
                                <div className="text-xs text-gray-500">{new Date(model.created_at).toLocaleDateString()}</div>
                                <div className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">대기 중</div>
                              </div>
                              <div className="space-y-2">
                                <div className="flex items-center text-xs text-gray-600">
                                  <Loader className="w-3 h-3 animate-spin mr-2" />
                                  큐에서 대기 중...
                                </div>
                                <div className="bg-gray-200 rounded-full h-1.5">
                                  <div className="bg-teal-500 h-1.5 rounded-full" style={{ width: "10%" }}></div>
                                </div>
                              </div>
                              <div className="text-xs text-[10px] text-gray-400 mt-2 text-center">생성ID: {model.uuid}</div>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}

                  {/* 생성 중 섹션 */}
                  {modelsToDisplay.some((m) => m.status === "processing") && (
                    <div>
                      <div className="flex items-center mb-2">
                        <span className="text-sm font-semibold text-gray-700">생성 중</span>
                        <span className="ml-2 text-xs text-gray-500">({modelsToDisplay.filter((m) => m.status === "processing").length})</span>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {modelsToDisplay
                          .filter((m) => m.status === "processing")
                          .map((model) => (
                            <div key={model.uuid} className="border rounded-lg p-4 pb-1 space-y-3 cursor-pointer hover:shadow-md transition-shadow" onClick={() => handleModelClick(model)}>
                              <div className="flex items-center justify-between">
                                <div className="text-xs text-gray-500">{new Date(model.created_at).toLocaleDateString()}</div>
                                <div className="px-2 py-1 text-xs rounded-full bg-yellow-100 text-yellow-800">생성 중</div>
                              </div>
                              <div className="space-y-2">
                                <div className="flex items-center text-xs text-gray-600">
                                  <Loader className="w-3 h-3 animate-spin mr-2" />
                                  생성 중...
                                </div>
                                <div className="bg-gray-200 rounded-full h-1.5">
                                  <div className="bg-teal-500 h-1.5 rounded-full animate-pulse" style={{ width: "65%" }}></div>
                                </div>
                              </div>
                              <div className="text-xs text-[10px] text-gray-400 mt-2 text-center">생성ID: {model.uuid}</div>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}

                  {/* 완료 및 실패 */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {modelsToDisplay
                      .filter((m) => m.status !== "queued" && m.status !== "processing")
                      .map((model) => (
                        <div key={model.uuid} className="border rounded-lg p-4 pb-1 space-y-3 cursor-pointer hover:shadow-md transition-shadow" onClick={() => handleModelClick(model)}>
                          <div className="flex items-center justify-between">
                            <div className="text-xs text-gray-500">{new Date(model.created_at).toLocaleDateString()}</div>
                            <div className={`px-2 py-1 text-xs rounded-full ${model.status === "completed" ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>{model.status === "completed" ? "완료" : "실패"}</div>
                          </div>
                          {model.status === "completed" && model.model_url && (
                            <div className="space-y-2">
                              <div className="border rounded-lg overflow-hidden">{model.preview_url ? <img src={model.preview_url} alt="AI 모델 프리뷰" className="w-full h-32 object-cover" /> : <model-viewer src={model.model_url} alt="AI 생성 3D 모델" auto-rotate camera-controls style={{ width: "100%", height: "128px" }} loading="eager"></model-viewer>}</div>
                            </div>
                          )}
                          {model.status === "failed" && (
                            <div className="text-center py-2">
                              <AlertCircle className="w-6 h-6 text-red-500 mx-auto mb-1" />
                              <p className="text-xs text-red-600">생성 실패</p>
                              {model.error_message && <p className="text-xs text-gray-500 mt-1">{model.error_message}</p>}
                            </div>
                          )}
                          <div className="text-xs text-[10px] text-gray-400 mt-2 text-center">생성ID: {model.uuid}</div>
                        </div>
                      ))}
                  </div>
                </>
              );
            })()}

            {userModels.length > 4 && (
              <div className="text-center">
                <button onClick={() => setShowAllModels((v) => !v)} className="px-4 py-2 text-sm text-teal-600 hover:text-teal-700">
                  {showAllModels ? "접기" : `더보기 (${userModels.length - 4}개 더)`}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* 불러오기 모달 */}
      {showLoadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium">AI 모델 불러오기</h3>
              <button onClick={() => setShowLoadModal(false)} className="text-gray-500 hover:text-gray-700">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-3">
              <label className="block text-sm text-gray-700">생성ID</label>
              <input value={loadId} onChange={(e) => setLoadId(e.target.value)} placeholder="예) GJRWCNTGMUYWEZA" className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500" />
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setShowLoadModal(false)} className="px-4 py-2 text-gray-600 hover:text-gray-800">
                취소
              </button>
              <button
                onClick={async () => {
                  if (!loadId.trim()) return;
                  try {
                    const res = await fetch(`${process.env.REACT_APP_API_BASE}/api/ai/model/${loadId.trim()}`, {
                      headers: { Authorization: localStorage.getItem("auth_token") },
                    });
                    const data = await res.json();
                    if (!res.ok || !data.success || !data.model) {
                      throw new Error(data.detail || "모델을 불러오지 못했습니다.");
                    }
                    // handleModelClick 에 넣기 위해 필드명 정렬
                    handleModelClick({
                      ...data.model,
                    });
                    setShowLoadModal(false);
                  } catch (err) {
                    console.error("모델 불러오기 오류:", err);
                    setError(err.message || "모델 불러오기 중 오류가 발생했습니다.");
                  }
                }}
                className="px-4 py-2 bg-teal-500 text-white rounded-md hover:bg-teal-600"
              >
                확인
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 업로드 성공 모달 */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md mx-4">
            <h3 className="text-lg font-medium mb-4 text-green-600">모델 생성 요청 완료!</h3>
            <p className="text-gray-600 mb-6">AI가 3D 모델을 생성하고 있습니다. 4~5분 정도 소요되며 완료 후 문자메세지로 안내드립니다.</p>
            <button onClick={() => setShowSuccessModal(false)} className="w-full px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors">
              확인
            </button>
          </div>
        </div>
      )}

      {/* 생성 중 모델 클릭 모달 */}
      {showProcessingModal && processingModel && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md mx-4">
            {processingModel.status === "queued" ? (
              <>
                <h3 className="text-lg font-medium mb-4 flex items-center">
                  <Loader className="w-5 h-5 animate-spin mr-2 text-teal-500" />
                  큐에서 대기 중
                </h3>
                <p className="text-gray-600 mb-2">요청이 접수되었습니다. 곧 생성이 시작됩니다.</p>
                <div className="text-sm text-gray-500 mb-4">
                  {typeof queueInfo.queuedCount === "number" && (
                    <div>
                      현재 전체 대기: <span className="font-semibold">{queueInfo.queuedCount}</span>건
                    </div>
                  )}
                  {queueInfo.queuePosition && (
                    <div>
                      내 대기 순서: <span className="font-semibold">{queueInfo.queuePosition}</span>
                    </div>
                  )}
                </div>
                <div className="bg-gray-200 rounded-full h-3 mb-6">
                  <div className="bg-teal-500 h-3 rounded-full" style={{ width: "10%" }}></div>
                </div>
              </>
            ) : (
              <>
                <h3 className="text-lg font-medium mb-4 flex items-center">
                  <Loader className="w-5 h-5 animate-spin mr-2 text-teal-500" />
                  3D 모델 생성 중
                </h3>
                <p className="text-gray-600 mb-4">AI가 열심히 3D 모델을 생성하고 있습니다.</p>
                <p className="text-sm text-gray-500 mb-6">생성 시작: {new Date(processingModel.created_at).toLocaleString()}</p>
                <div className="bg-gray-200 rounded-full h-3 mb-6">
                  <div className="bg-teal-500 h-3 rounded-full animate-pulse" style={{ width: "65%" }}></div>
                </div>
              </>
            )}
            <button onClick={() => setShowProcessingModal(false)} className="w-full px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors">
              닫기
            </button>
          </div>
        </div>
      )}

      {/* 완료된 모델 상세 모달 */}
      {showCompletedModal && selectedModel && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-6xl min-h-[60vh] w-full max-h-screen overflow-auto relative mx-4 flex flex-col">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold">AI 생성 3D 모델</h3>
              <button onClick={() => setShowCompletedModal(false)} className="text-gray-500 hover:text-gray-700 p-1">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-7 gap-6 flex-1">
              {/* 좌측: 3D 모델 뷰어 (모바일: 전체, 데스크톱: 5/7) */}
              <div className="col-span-1 lg:col-span-5 flex flex-col">
                <div className="flex-1 min-h-64 w-full border rounded-lg overflow-hidden bg-gray-50">
                  <model-viewer src={selectedModel.model_url} alt="AI 생성 3D 모델" auto-rotate camera-controls style={{ width: "100%", height: "100%" }} loading="eager"></model-viewer>
                </div>
              </div>

              {/* 우측: 주문시 유의사항 (모바일: 전체, 데스크톱: 2/7) */}
              <div className="col-span-1 lg:col-span-2 space-y-4 flex flex-col justify-start">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">주문시 유의사항</h4>
                  <div className="text-sm text-gray-600 space-y-2">
                    <ul className="list-none">
                      <li className="flex items-start">
                        <span className="mr-2">•</span>
                        <span>AI로 생성된 3D 모델입니다</span>
                      </li>
                      <li className="flex items-start">
                        <span className="mr-2">•</span>
                        <span>실제 제작물과 차이가 있을 수 있습니다</span>
                      </li>
                      <li className="flex items-start">
                        <span className="mr-2">•</span>
                        <span>AI 생성 모델 특성상, 사이즈가 고정되어있지 않아 형상과 무관하게 가장 긴 선을 기준으로 5cm 사이즈로 제작합니다.</span>
                      </li>
                      <li className="flex items-start">
                        <span className="mr-2">•</span>
                        <span>풀 컬러로 제작이 진행되며, 레진 소재를 사용하여 제작합니다.</span>
                      </li>
                      <li className="flex items-start">
                        <span className="mr-2">•</span>
                        <span>크기 조정이나 단색 제작 요청 등 추가 요청사항이 있으시면 우측 하단 채널톡으로 문의 부탁드립니다.</span>
                      </li>
                    </ul>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <h4 className="font-semibold text-gray-900 mb-2">모델 정보</h4>
                  <div className="text-sm text-gray-600 space-y-1">
                    <p>생성ID: {selectedModel.uuid}</p>
                    <p>소유자: {selectedModel.user_id}</p>
                    <p>생성 완료: {new Date(selectedModel.completed_at || selectedModel.created_at).toLocaleDateString()}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* 하단 액션 버튼 */}
            <div className="flex justify-between mt-6 pt-4 border-t">
              <button onClick={() => setShowCompletedModal(false)} className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors">
                닫기
              </button>
              <button
                onClick={() => {
                  addToCart(selectedModel.uuid);
                  setShowCompletedModal(false);
                }}
                className="flex items-center px-6 py-2 bg-teal-500 text-white rounded-lg font-medium hover:bg-teal-600 transition-colors"
              >
                <ShoppingCart className="w-4 h-4 mr-2" />
                장바구니에 추가
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 사용량 초과 모달 */}
      {showUsageModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md mx-4">
            <h3 className="text-lg font-medium mb-4">일일 사용량 초과</h3>
            <p className="text-gray-600 mb-6">하루에 최대 3개의 AI 모델을 생성할 수 있습니다. 채팅상담을 통해 고객센터에 문의하거나, 내일 다시 시도해주세요.</p>
            <button onClick={() => setShowUsageModal(false)} className="w-full px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors">
              확인
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AI3DModeling;
